import { FC } from 'react'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { GRAY_400, GRAY_700 } from 'constants/styling/theme'

export type UserAvatarChipSize = 'xs' | 'sm' | 'md' | 'lg'

const AVATAR_SIZE: Record<UserAvatarChipSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 28
}

const GAP: Record<UserAvatarChipSize, string> = {
  xs: '.4rem',
  sm: '.6rem',
  md: '.6rem',
  lg: '.8rem'
}

interface Props {
  /** User display name */
  name: string
  /** Avatar image URL */
  avatarUrl?: string
  /** Size variant matching badge sizes */
  size?: UserAvatarChipSize
  /** Text color override */
  textColor?: string
  /** Font weight override */
  fontWeight?: number
  /** Show border around the chip */
  bordered?: boolean
  /** Avatar alone, without the name text: the name becomes the avatar's accessible name */
  isAvatarOnly?: boolean
  /** Avatar size in px, overriding `size` */
  avatarSize?: number
}

/**
 * Compact user avatar + name chip for inline display.
 *
 * @example
 * <UserAvatarChip name="John Doe" avatarUrl="https://..." bordered />
 */
export const UserAvatarChip: FC<Props> = ({
  name,
  avatarUrl,
  size = 'sm',
  textColor = GRAY_700,
  fontWeight = 450,
  bordered = false,
  isAvatarOnly = false,
  avatarSize,
}) => {
  const avatarPx = avatarSize ?? AVATAR_SIZE[size]

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={GAP[size]}
      display="inline-flex"
      sx={bordered ? {
        border: `1px solid ${GRAY_400}`,
        borderRadius: '.4rem',
        padding: '.4rem'
      } : undefined}
    >
      <Avatar
        src={avatarUrl}
        // Avatar only: one accessible name on the root, whether the photo or the initial (its fallback) shows
        alt={isAvatarOnly ? '' : name}
        sx={{ width: avatarPx, height: avatarPx, fontSize: avatarPx * 0.5 }}
        {...(isAvatarOnly && { role: 'img', 'aria-label': name })}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>
      {!isAvatarOnly &&
        <Typography variant="text-sm" fontWeight={fontWeight} color={textColor}>
          {name}
        </Typography>
      }
    </Stack>
  )
}
